import React, { useCallback, useState } from 'react';
import { content } from '../data';
import { motion, AnimatePresence } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, Star, X, MessageSquareQuote } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';

const Testimonials = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'start',
    slidesToScroll: 1,
    breakpoints: {
      '(min-width: 768px)': { slidesToScroll: 2 },
      '(min-width: 1024px)': { slidesToScroll: 3 }
    }
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const openModal = (index) => {
    setSelectedIndex(index);
  };

  const closeModal = () => {
    setSelectedIndex(null);
  };

  const nextTestimonial = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev + 1) % content.testimonials.length);
  };

  const prevTestimonial = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev - 1 + content.testimonials.length) % content.testimonials.length);
  };

  const selectedTestimonial = selectedIndex !== null ? content.testimonials[selectedIndex] : null;

  return (
    <section className="py-16 bg-blue-950 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-5xl font-semibold uppercase tracking-tighter italic">Mød mine fantastiske elever</h2>
            <p className="mt-4 text-blue-200/80 text-xl font-light">De tror på mig, gør du?</p>
          </div>
          
          {content.testimonials.length > 1 && (
            <div className="flex gap-4">
              <button 
                onClick={scrollPrev}
                className="p-3 border border-white/10 hover:border-white/40 bg-white/5 hover:bg-white/10 rounded-full transition-all duration-300"
                aria-label="Forrige"
              >
                <ChevronLeft size={24} />
              </button>
              <button 
                onClick={scrollNext}
                className="p-3 border border-white/10 hover:border-white/40 bg-white/5 hover:bg-white/10 rounded-full transition-all duration-300"
                aria-label="Næste"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          )}
        </div>

        <div className="embla overflow-hidden" ref={emblaRef}>
          <div className={`embla__container flex -ml-4 ${content.testimonials.length === 1 ? 'justify-center' : ''}`}>
            {content.testimonials.map((item, index) => (
              <div 
                key={index} 
                className={`embla__slide min-w-0 pl-4 ${
                  content.testimonials.length === 1 
                    ? 'flex-[0_0_100%] max-w-2xl' 
                    : 'flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%]'
                }`}
              >
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="h-full flex flex-col relative group pt-4 bg-white/5 p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-500 cursor-pointer"
                  onClick={() => openModal(index)}
                >
                  <div className="flex gap-1 mb-4 text-yellow-400/90">
                    {[...Array(item.rating || 5)].map((_, i) => (
                      <Star key={i} size={14} fill="currentColor" className="stroke-none" />
                    ))}
                  </div>
                  <div className="flex-grow">
                    <p className="text-blue-50/90 leading-relaxed font-light line-clamp-4 italic text-lg relative z-10">
                      "{item.text}"
                    </p>
                    {item.text.length > 150 && (
                      <button 
                        className="mt-4 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-2 group/btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(index);
                        }}
                      >
                        Læs mere 
                        <ChevronRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    )}
                  </div>
                  <div className="mt-8 border-t border-white/5 pt-6 flex justify-between items-end">
                    <div>
                      <h3 className="text-lg font-semibold uppercase text-white tracking-tight">{item.name}</h3>
                      <p className="text-xs text-blue-400/60 mt-0.5 uppercase tracking-widest font-medium">Tidligere Elev</p>
                    </div>
                    {item.date && (
                      <span className="text-[10px] text-white/20 uppercase tracking-tighter font-semibold">{item.date}</span>
                    )}
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog.Root open={selectedIndex !== null} onOpenChange={(open) => !open && closeModal()}>
        <AnimatePresence>
          {selectedIndex !== null && selectedTestimonial && (
            <Dialog.Portal forceMount>
              <Dialog.Overlay asChild forceMount>
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-blue-950/80 backdrop-blur-sm z-[100]"
                />
              </Dialog.Overlay>
              <Dialog.Content asChild forceMount>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-blue-900 border border-white/10 rounded-3xl shadow-2xl p-8 md:p-12 z-[101] focus:outline-none"
                >
                  <Dialog.Title className="sr-only">
                    Anmeldelse fra {selectedTestimonial.name}
                  </Dialog.Title>
                  <Dialog.Description className="sr-only">
                    Læs hele anmeldelsen fra {selectedTestimonial.name} om deres forløb hos Lenes Køreskole.
                  </Dialog.Description>

                  <Dialog.Close className="absolute top-6 right-6 p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-full transition-all">
                    <X size={24} />
                  </Dialog.Close>

                  <div className="relative">
                    <div className="flex gap-1 mb-8 text-yellow-400">
                      {[...Array(selectedTestimonial.rating || 5)].map((_, i) => (
                        <Star key={i} size={18} fill="currentColor" className="stroke-none" />
                      ))}
                    </div>

                    <MessageSquareQuote size={48} className="text-white/5 absolute -top-4 -left-4 pointer-events-none" />
                    
                    <div className="text-xl md:text-2xl text-blue-50 font-light leading-relaxed italic mb-10">
                      "{selectedTestimonial.text}"
                    </div>

                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-t border-white/10 pt-8">
                      <div>
                        <h3 className="text-2xl font-bold text-white uppercase tracking-tight">{selectedTestimonial.name}</h3>
                        <p className="text-blue-400/80 mt-1 uppercase tracking-widest text-sm font-medium">Tidligere Elev hos Lene</p>
                      </div>
                      {selectedTestimonial.date && (
                        <span className="text-sm text-white/30 font-medium italic">{selectedTestimonial.date}</span>
                      )}
                    </div>

                    <div className="flex justify-between mt-12 gap-4">
                      <button 
                        onClick={prevTestimonial}
                        className="flex-1 flex items-center justify-center gap-2 py-4 border border-white/10 hover:bg-white/5 rounded-2xl transition-all font-medium uppercase text-xs tracking-widest text-white"
                      >
                        <ChevronLeft size={16} />
                        Forrige
                      </button>
                      <button 
                        onClick={nextTestimonial}
                        className="flex-1 flex items-center justify-center gap-2 py-4 border border-white/10 hover:bg-white/5 rounded-2xl transition-all font-medium uppercase text-xs tracking-widest text-white"
                      >
                        Næste
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              </Dialog.Content>
            </Dialog.Portal>
          )}
        </AnimatePresence>
      </Dialog.Root>
    </section>
  );
};

export default Testimonials;
